import ScheduledMessagesCount from "@common-components/scheduled-messages-count/scheduled-messages-count";
import ContactAvatar from '@extension/context/content/components/bulk-dashboard/contact-table/contact-avatar';
import { Box, SxProps, Theme } from "@mui/material";

import ScheduledMessageDateBadge from '@common-components/scheduled-message-date-badge/scheduled-message-date-badge';
import { DeleteMessageButton } from '@common/components/delete-message-button/delete-message-button';
import { MoreVertical, Repeat } from "lucide-react";

interface WhatsappContactProps {
  whatsappId?: string;
  contactName?: string;
  message?: string;
  messageScheduledCount?: number;
  messageApiCount?: number;
  messageCampaignCount?: number;
  dueDate?: string;
  justifyContentType?: 'space-between' | 'center',
  showMessageDetails?: boolean
  profilePicURL?: string;
  sx?: SxProps<Theme>;
  messageData?: any; // Full message data for delete functionality
  showDeleteButton?: boolean;
  showMenuButton?: boolean;
  onDelete?: (message: any) => void;
  onMenuClick?: () => void; // Callback when menu button is clicked
  menuButtonRef?: React.RefObject<HTMLDivElement>; // Ref for menu button positioning
  isRecurring?: boolean; // Whether the message is recurring
}

const WhatsappContactBox = ({
  message,
  contactName: _contactName,
  whatsappId,
  messageScheduledCount,
  messageApiCount,
  messageCampaignCount,
  dueDate,
  justifyContentType = 'space-between',
  showMessageDetails = true,
  profilePicURL,
  sx = {},
  messageData,
  showDeleteButton = false,
  showMenuButton = false,
  onDelete,
  onMenuClick,
  menuButtonRef,
  isRecurring = false
}: WhatsappContactProps) => {
  // Use the passed contactName directly since we've calculated it correctly in the parent
  const contactName = _contactName;


  return (


    <Box sx={{
      width: "100%",
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: 1.5,
      position: 'relative',
      boxSizing: 'border-box',
     
      '&:hover': {
        '& .delete-button': {
          opacity: 1
        },
        '& .menu-button': {
          opacity: 1
        }
      },
      ...sx
    }}>
      {/* Avatar (green area) */}
      <ContactAvatar
        sx={{
          flexShrink: 0,
        }}
        contactId={messageData?.contactList?.[0]?.id || whatsappId}
        size={40}
        textSize={11}
        profilePicURL={profilePicURL}
      />

      {showMessageDetails && (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          width: 'calc(100% - 56px)', // Account for avatar width and gap
          gap: 1,
        }}>
          {/* Text box (red area) */}
          <Box
            sx={{
              overflow: 'hidden',
              minWidth: 0,
              width: '100%'
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              lineHeight: 1.2,
              mb: 0.5,
              gap: 1,
              width: '100%',
            }}>
              {/* Contact name - left side */}
              <Box sx={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--tasks-text-secondary)',
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                minWidth: 0,
                flex: 1,
              }}>
                {contactName}
              </Box>

              {/* Right side: recurring icon */}
              {isRecurring && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  flexShrink: 0,
                }}>
                  <Repeat
                    size={12}
                    style={{
                      color: 'var(--color-text-muted)',
                      opacity: 0.7,
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Message text */}
            <Box sx={{
              fontSize: '13px',
              color: 'var(--tasks-text-secondary)',
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
              lineHeight: 1.2,
            }}>
              {message || 'No recent message'}
            </Box>

            {/* Bottom row with counters and date badge (blue area) */}
            <Box
              sx={{
                marginTop: '7px',
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                gap: 0.5
              }}
            >
              {/* Counters */}
              {messageScheduledCount ? <ScheduledMessagesCount title="SCHEDULED" countValue={messageScheduledCount} /> : null}
              {messageApiCount ? <ScheduledMessagesCount title="API" countValue={messageApiCount} /> : null}
              {messageCampaignCount ? <ScheduledMessagesCount title="CAMPAIGN" countValue={messageCampaignCount} /> : null}

              <ScheduledMessageDateBadge
                dueDate={dueDate}
                status={messageData?.status}
                error={messageData?.sendFailureError}
                message={messageData}
              />
            </Box>
          </Box>

          {/* Delete button */}
          {showDeleteButton && messageData && (
            <Box
              sx={{
                position: 'absolute',
                insetInlineEnd: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                className: 'delete-button',
                '.MuiButtonBase-root:hover &': {
                  opacity: 1
                },
                backgroundColor: 'var(--tasks-row-hover-bg)',
                borderRadius: '4px',
                padding: '2px',
              }}
            >
              <DeleteMessageButton
                message={messageData}
                size={18}
                onDelete={onDelete}
                style={{
                  cursor: 'pointer',
                  padding: '4px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--color-primary)',
                  backgroundColor: 'var(--tasks-row-hover-bg)',
                }}
              />
            </Box>
          )}

          {/* Menu button (3 dots) */}
          {showMenuButton && (
            <Box
              ref={menuButtonRef}
              onClick={(e) => {
                e.stopPropagation();
                onMenuClick?.();
              }}
              sx={{
                position: 'absolute',
                insetInlineEnd: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1,
                backgroundColor: 'var(--tasks-row-hover-bg)',
                borderRadius: '4px',
                padding: '2px',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'var(--color-surface-muted)',
                },
              }}
              className="menu-button"
              title="Open menu"
            >
              <MoreVertical size={18} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default WhatsappContactBox;
